import { Chip, Tooltip } from "@mui/material";
import { IPipelineServiceStatus, IPipelineStatus } from "../../types/api/pipeline.type";

/** Services an operator starts/stops/scales from the Services tab, in display order. */
export const CONTROLLABLE_SERVICES = ["resample", "score", "alert", "retry", "derived_tag"];

/** Every service the pipeline reports, for the list page's health column. */
export const ALL_SERVICES = [...CONTROLLABLE_SERVICES, "maintenance"];

export const DEFAULT_STARTPOINT: Record<string, string> = {
  resample: "now",
  score: "queue",
  alert: "queue",
  derived_tag: "now",
  retry: "queue",
};

/**
 * derived_tag has no worker pool — its "workers" value is the concurrency limit
 * on parallel rule evaluations, so it is labelled as such.
 */
export const workerLabel = (service: string) => (service === "derived_tag" ? "Concurrency" : "Workers");

export interface IStartpointOption {
  value: string;
  label: string;
  destructive?: boolean;
}

/**
 * "queue" means different things per service: for resample it CLEARS the stream
 * and then resumes from the last watermark, while for the others it processes
 * the existing backlog only. Options are per service so the label is accurate.
 */
export const startpointOptions = (service: string): IStartpointOption[] =>
  service === "resample"
    ? [
        { value: "now", label: "now — skip backlog, start fresh" },
        { value: "last", label: "last — resume from last watermark, keeps queue" },
        {
          value: "queue",
          label: "queue — clears the stream, then resumes from last watermark",
          destructive: true,
        },
        { value: "custom", label: "custom timestamp…" },
      ]
    : [
        { value: "queue", label: "queue — process existing backlog only" },
        { value: "now", label: "now — skip backlog, start fresh" },
        { value: "last", label: "last — resume from last watermark" },
        { value: "custom", label: "custom timestamp…" },
      ];

/** datetime-local gives "YYYY-MM-DDTHH:MM" with no zone; the pipeline expects UTC ISO. */
export const localDatetimeToUtcIso = (value: string) => (value ? `${value}:00Z` : undefined);

type ChipColor = "success" | "warning" | "error" | "default" | "info";

interface IServiceState {
  label: string;
  color: ChipColor;
  /** Higher is worse; used to pick a pipeline's overall state. */
  rank: number;
}

/**
 * A stopped service can still report health "healthy" (health only looks at
 * DLQ/PEL/failure rate), so "stopped" wins over the health value.
 */
export const serviceState = (s?: IPipelineServiceStatus): IServiceState => {
  if (!s) return { label: "unknown", color: "default", rank: 1 };
  if (s.error) return { label: "error", color: "error", rank: 4 };
  if (!s.running) return { label: "stopped", color: "default", rank: 0 };
  if (s.alive === false) return { label: "crashed", color: "error", rank: 4 };
  switch (s.health) {
    case "critical":
      return { label: "critical", color: "error", rank: 3 };
    case "degraded":
      return { label: "degraded", color: "warning", rank: 2 };
    default:
      return { label: s.health || "healthy", color: "success", rank: 0 };
  }
};

export const ServiceStateChip = ({ name, status }: { name?: string; status?: IPipelineServiceStatus }) => {
  const state = serviceState(status);
  return (
    <Tooltip title={status?.error ?? (name ? `${name}: ${state.label}` : state.label)}>
      <Chip
        size="small"
        label={name ? `${name} · ${state.label}` : state.label}
        color={state.color}
        variant={state.color === "default" ? "outlined" : "filled"}
        sx={{ mr: 0.5, mb: 0.5 }}
      />
    </Tooltip>
  );
};

/** One overall chip for a pipeline: the worst state of its running services. */
export const overallState = (status?: IPipelineStatus | null, unreachable?: boolean): IServiceState => {
  if (unreachable) return { label: "unreachable", color: "error", rank: 5 };
  if (!status) return { label: "loading", color: "default", rank: 0 };
  const states = Object.values(status.services ?? {}).map(serviceState);
  if (states.length === 0) return { label: "unknown", color: "default", rank: 1 };
  const running = Object.values(status.services).filter((s) => s.running).length;
  if (running === 0) return { label: "all stopped", color: "default", rank: 1 };
  const worst = states.reduce((a, b) => (b.rank > a.rank ? b : a));
  return worst.rank === 0 ? { label: "healthy", color: "success", rank: 0 } : worst;
};
